/**
 * Maintenance-tracker → guide library matching.
 *
 * The maintenance tracker (lib/maintenanceSchedule.js) defines GENERIC service
 * items keyed by simple slugs like 'oil-change', 'air-filter', 'brake-pads'.
 *
 * The guide library (lib/guideDataTJ.js) contains VEHICLE-SPECIFIC guides
 * with IDs like 'tj-oil-change-4pt0l-i6'. Some maintenance items map to
 * multiple guides (e.g. oil change has both 2.4L and 4.0L variants on TJ).
 *
 * This file is the single source of truth for that mapping. As we add new
 * vehicle support, append entries here — guides on the LEFT, the maintenance
 * item ids they satisfy on the RIGHT.
 *
 * The runtime resolver `findGuideForMaintenanceItem` uses
 * `guideMatchesVehicle` to pick the correct guide for the user's active
 * vehicle (engine, transmission, year all factor in).
 */

import { guideMatchesVehicle } from './guideMatching.js';

// ── GUIDE → MAINTENANCE/INSPECTION ITEM MAP ──────────────────────────
// Each guide id maps to an array of tracker item ids that it satisfies.
//
// IMPORTANT: only link a guide to a tracker item of the SAME type:
//   - Maintenance tracker items (oil-change, brake-pads, etc.) → maintenance/repair guides
//   - Inspection tracker items (brake-pad-inspection, fluid-level-checks, cv-joint-boot-check,
//     etc.) → inspection guides ONLY (e.g. tj-pre-trip-pre-purchase-inspection,
//     tj-cv-joint-inspection, tj-radiator-cap-pressure-test)
//
// Inspections and replacements are different procedures with different outcomes;
// a brake pad replacement guide is NOT what a user needs when they want to inspect
// pad thickness. Keep them separate.
//
// To add support for a new vehicle's guide library, append entries below.
export const GUIDE_TO_MAINTENANCE_ITEMS = {
  // ════════════════════════════════════════════════════════════════
  // MAINTENANCE GUIDES — only true scheduled-interval services.
  // No wear-item replacements (brake pads, tires, water pump, etc.) —
  // those are repair work, not maintenance, per V10.2 rules.
  // ════════════════════════════════════════════════════════════════
  // Generic services
  'tj-tire-rotation':                          ['tire-rotation'],
  'tj-engine-air-filter-replacement':          ['air-filter'],
  'tj-cabin-air-filter-replacement':           ['cabin-filter'],
  'tj-brake-fluid-flush-bleed':                ['brake-fluid'],
  'tj-coolant-drain-fill':                     ['coolant'],
  'tj-transmission-fluid-manual':              ['transmission-fluid-manual'],
  'tj-transmission-fluid-automatic':           ['transmission-fluid-automatic'],
  'tj-spark-plug-replacement':                 ['spark-plugs'],
  'tj-fuel-filter-replacement':                ['fuel-filter-replacement'],

  // TJ-specific scheduled services
  'tj-transfer-case-fluid-change':             ['tj-transfer-case-fluid'],
  'tj-front-differential-fluid-change-dana-30':['tj-front-differential-fluid'],
  'tj-rear-differential-fluid-change':         ['tj-rear-differential-fluid'],
  'tj-power-steering-fluid-change':            ['tj-power-steering-fluid'],
  'tj-distributor-cap-rotor-replacement-4pt0l-i6': ['tj-distributor-cap-rotor'],
  'tj-pcv-valve-replacement-4pt0l-i6':         ['tj-pcv-valve'],
  'tj-throttle-body-cleaning-4pt0l-i6':        ['tj-throttle-body-cleaning'],
  'tj-timing-belt-replacement-2pt4l-i4':       ['tj-timing-belt-2pt4l'],
  'tj-auto-trans-pan-gasket-filter':           ['transmission-fluid-automatic'], // alternate guide for ATF service

  // Engine-specific oil change (matcher picks correct one based on active engine)
  'tj-oil-change-2pt4l':                       ['oil-change'],
  'tj-oil-change-4pt0l-i6':                    ['oil-change'],
  // Engine-specific spark plug
  'tj-spark-plug-replacement-4pt0l-i6':        ['spark-plugs'],

  // ════════════════════════════════════════════════════════════════
  // INSPECTION GUIDES — visual / sensor checks
  // ════════════════════════════════════════════════════════════════
  // Overall vehicle inspection (the "big one" — comprehensive walkthrough)
  'tj-pre-trip-pre-purchase-inspection': [
    'overall-vehicle-inspection',
    'fluid-level-checks',
    'tire-pressure-checks',
    'tire-tread-inspection',
    'wiper-blade-inspection',
    'battery-terminal-inspection',
    'belt-and-hose-inspection',
    'brake-pad-inspection',
    'steering-suspension-check',
    'wheel-bearing-check',
    'exhaust-system-check',
    'cooling-system-check',
    'seatbelt-check',
    'tj-frame-rust-inspection',
    'tj-axle-vent-inspection',
  ],
  // Dedicated inspection guides cover specific items more thoroughly
  'tj-cv-joint-inspection':                    ['cv-joint-boot-check'],
  'tj-radiator-cap-pressure-test':             ['cooling-system-check'],

  // ════════════════════════════════════════════════════════════════
  // BULLNOSE F-SERIES (1980-1986) — guide → tracker item mappings
  // ════════════════════════════════════════════════════════════════
  // Generic services (per-engine oil change resolved by engine matcher)
  'bullnose-oil-change-4pt9l-i6':              ['oil-change'],
  'bullnose-oil-change-5pt0l-v8':              ['oil-change'],
  'bullnose-oil-change-5pt8l-v8':              ['oil-change'],
  'bullnose-oil-change-7pt5l-v8':              ['oil-change'],
  'bullnose-oil-change-6pt9l-diesel':          ['oil-change'],

  'bullnose-engine-air-filter':                ['air-filter'],
  'bullnose-tire-rotation':                    ['tire-rotation'],
  'bullnose-coolant-drain-fill':               ['coolant'],
  'bullnose-brake-fluid-flush':                ['brake-fluid'],
  'bullnose-spark-plug-replacement-i6':        ['spark-plugs', 'bullnose-spark-plug-i6'],
  'bullnose-spark-plug-replacement-v8':        ['spark-plugs', 'bullnose-spark-plug-v8'],

  // Bullnose-specific scheduled services
  'bullnose-trans-fluid-c6':                   ['transmission-fluid-automatic', 'bullnose-trans-fluid-c6'],
  'bullnose-trans-fluid-aod':                  ['transmission-fluid-automatic', 'bullnose-trans-fluid-aod'],
  'bullnose-trans-fluid-manual-np435':         ['transmission-fluid-manual'],
  'bullnose-front-diff-fluid-dana-44':         ['bullnose-front-diff-fluid-dana-44'],
  'bullnose-front-diff-fluid-dana-60':         ['bullnose-front-diff-fluid-dana-44'],
  'bullnose-rear-diff-fluid-9-inch':           ['bullnose-rear-diff-fluid-9inch'],
  'bullnose-rear-diff-fluid-sterling':         ['bullnose-rear-diff-fluid-9inch'],
  'bullnose-transfer-case-np205':              ['bullnose-transfer-case-np205'],
  'bullnose-transfer-case-bw1345':             ['bullnose-transfer-case-np205'],
  'bullnose-power-steering-fluid':             ['bullnose-power-steering-fluid'],
  'bullnose-pcv-valve':                        ['bullnose-pcv-valve'],
  'bullnose-fuel-filter-gas':                  ['fuel-filter-replacement', 'bullnose-fuel-filter-gas'],
  'bullnose-diesel-fuel-filter':               ['bullnose-diesel-fuel-filter'],

  // Bullnose inspection guides
  'bullnose-pre-trip-pre-purchase-inspection': [
    'overall-vehicle-inspection',
    'fluid-level-checks',
    'tire-pressure-checks',
    'tire-tread-inspection',
    'wiper-blade-inspection',
    'battery-terminal-inspection',
    'belt-and-hose-inspection',
    'brake-pad-inspection',
    'steering-suspension-check',
    'wheel-bearing-check',
    'exhaust-system-check',
    'cooling-system-check',
    'seatbelt-check',
  ],
  'bullnose-frame-rust-inspection':            ['frame-rust-inspection'],
  'bullnose-cab-corner-rust-inspection':       ['body-rust-inspection'],
  'bullnose-ttb-axle-inspection':              ['cv-joint-boot-check', 'steering-suspension-check'],
  'bullnose-cooling-system-pressure-test':     ['cooling-system-check'],

  // Future vehicle libraries: append entries here following the same pattern.
};

/**
 * Reverse index: maintenance item id → array of candidate guide ids.
 * Built once at module load.
 */
const MAINTENANCE_ITEM_TO_GUIDE_IDS = (() => {
  const idx = {};
  for (const [guideId, itemIds] of Object.entries(GUIDE_TO_MAINTENANCE_ITEMS)) {
    for (const itemId of itemIds) {
      if (!idx[itemId]) idx[itemId] = [];
      idx[itemId].push(guideId);
    }
  }
  return idx;
})();

/**
 * Find the best-matching guide for a maintenance tracker item, given the
 * user's active vehicle. Uses `guideMatchesVehicle` so engine-specific guides
 * are picked correctly when the active vehicle has an engine selected.
 *
 * @param {string} maintenanceItemId — id from maintenanceSchedule.js
 * @param {object|null} vehicle — active vehicle (make/model/year/engine/...)
 * @param {object[]} allGuides — full guide library
 * @returns {object|null} matching guide, or null if none matches
 */
export function findGuideForMaintenanceItem(maintenanceItemId, vehicle, allGuides) {
  if (!maintenanceItemId || !vehicle || !Array.isArray(allGuides)) return null;

  const candidateIds = MAINTENANCE_ITEM_TO_GUIDE_IDS[maintenanceItemId];
  if (!candidateIds || candidateIds.length === 0) return null;

  // Find guides that are both registered for this maintenance item AND
  // match the user's active vehicle (year, engine, transmission).
  const matches = allGuides.filter(g =>
    candidateIds.includes(g.id) && guideMatchesVehicle(g, vehicle)
  );

  if (matches.length === 0) return null;

  // Prefer engine-specific guides ONLY when the user has chosen an engine
  // for their vehicle. Otherwise prefer engine-agnostic guides so the user
  // sees a guide that's valid regardless of their unrevealed engine variant.
  matches.sort((a, b) => {
    const aSpecific = a.vehicle?.engine ? 1 : 0;
    const bSpecific = b.vehicle?.engine ? 1 : 0;
    return vehicle.engine
      ? (bSpecific - aSpecific)  // engine known → engine-specific first
      : (aSpecific - bSpecific); // engine unknown → engine-agnostic first
  });

  return matches[0];
}

/**
 * Reverse helper: given a guide id, return the maintenance item ids it covers.
 * Useful for showing "this guide is part of your X service" labels.
 *
 * @param {string} guideId
 * @returns {string[]} array of maintenance item ids (empty if none)
 */
export function getMaintenanceItemsForGuide(guideId) {
  return GUIDE_TO_MAINTENANCE_ITEMS[guideId] || [];
}

/**
 * Returns true if any guide in the library covers this maintenance item for
 * the given vehicle. Used by the tracker UI to decide whether to render the
 * "View Guide" button at all.
 */
export function hasGuideForMaintenanceItem(maintenanceItemId, vehicle, allGuides) {
  return findGuideForMaintenanceItem(maintenanceItemId, vehicle, allGuides) !== null;
}
